import { reaction } from "mobx";
import { observer, Observer } from "mobx-react-lite";
import { ReactElement, useCallback, useEffect, useState } from "react";

import {
  Card,
  DetailsList,
  IconButton,
  Link,
  PrimaryButton,
  Stack
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { AppointmentCancellationReason } from "@stores/booking/models/AppointmentCancellationReason.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { When } from "@ui-components/withPerm.tsx";

import { AppointmentCancellationReasonDialog } from "./AppointmentCancellationReasonDialog.tsx";
import { DeleteAppointmentCancellationReasonDialog } from "./DeleteAppointmentCancellationReasonDialog.tsx";

export const AppointmentCancellationReasonList = observer(() => {
  const { booking, notification } = useStores();
  const [editReason, setEditReason] = useState<
    AppointmentCancellationReason | undefined
  >();

  const [deleteReason, setDeleteReason] = useState<
    AppointmentCancellationReason | undefined
  >();

  const [isEditDialogHidden, setIsEditDialogHidden] = useState(true);
  const [isDeleteDialogHidden, setIsDeleteDialogHidden] = useState(true);
  const [calendarCancellationReasons, setCalendarCancellationReason] = useState<
    AppointmentCancellationReason[]
  >([]);

  useEffect(() => {
    const disposer = reaction(
      () => Array.from(booking.appointmentCancellationReasonsMap.values()),
      setCalendarCancellationReason
    );
    return disposer;
  }, [booking.appointmentCancellationReasonsMap]);

  reaction(
    () => Array.from(booking.appointmentCancellationReasonsMap.values()),
    setCalendarCancellationReason
  );

  const toggleEditDialog = (reason?: AppointmentCancellationReason) => {
    if (isEditDialogHidden) {
      setEditReason(reason);
    } else {
      setEditReason(undefined);
    }
    setIsEditDialogHidden(!isEditDialogHidden);
  };

  const toggleDeleteDialog = (reason?: AppointmentCancellationReason) => {
    if (isDeleteDialogHidden) {
      setDeleteReason(reason);
    } else {
      setDeleteReason(undefined);
    }
    setIsDeleteDialogHidden(!isDeleteDialogHidden);
  };

  const fetchReasons = useCallback(async () => {
    const results = await booking.getAppointmentCancellationReasons({});
    setCalendarCancellationReason(results);
  }, [booking]);

  const onDelete = async () => {
    if (deleteReason) {
      await booking.deleteAppointmentCancellationReason(deleteReason.id);
    }
    notification.success("Appointment cancellation reason deleted");
    await fetchReasons();
    toggleDeleteDialog();
  };

  const renderCell = (render: () => ReactElement) => (
    <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 8 }}>
      <Observer>{render}</Observer>
    </Stack>
  );

  return (
    <Card
      iconName="DeactivateOrders"
      headingLevel="section-heading"
      heading="Appointment cancellation reasons"
      button={
        <When permission={Permission.OrgUnitSettingWrite}>
          <PrimaryButton
            iconProps={{
              iconName: "Add"
            }}
            text="Add cancellation reason"
            onClick={() => toggleEditDialog()}
          />
        </When>
      }
    >
      <Stack horizontal horizontalAlign="space-between">
        <DataFetcher fetch={fetchReasons}>
          {() => (
            <DetailsList
              styles={{
                root: {
                  ".ms-DetailsRow-cell": { whiteSpace: "initial" }
                }
              }}
              items={calendarCancellationReasons}
              setKey="id"
              columns={[
                {
                  name: "Cancellation reason",
                  key: "reason",
                  minWidth: 400,
                  maxWidth: 800,
                  onRender: (item: AppointmentCancellationReason) =>
                    renderCell(() => (
                      <Link onClick={() => toggleEditDialog(item)}>
                        {item.text}
                      </Link>
                    ))
                },
                {
                  name: "Status",
                  key: "status",
                  minWidth: 75,
                  maxWidth: 75,
                  onRender: (item: AppointmentCancellationReason) =>
                    renderCell(() => (
                      <>{item.isInactive ? "Inactive" : "Active"}</>
                    ))
                },
                {
                  name: "",
                  key: "delete",
                  minWidth: 40,
                  maxWidth: 40,
                  onRender: (item: AppointmentCancellationReason) =>
                    item.isSystemManaged ? (
                      <Stack styles={{ root: { height: 32 } }} />
                    ) : (
                      renderCell(() => (
                        <IconButton
                          iconProps={{ iconName: "Delete" }}
                          onClick={() => toggleDeleteDialog(item)}
                        />
                      ))
                    )
                }
              ]}
            />
          )}
        </DataFetcher>
      </Stack>
      <AppointmentCancellationReasonDialog
        hidden={isEditDialogHidden}
        onDismiss={toggleEditDialog}
        reason={editReason}
      />
      {!isDeleteDialogHidden && (
        <DeleteAppointmentCancellationReasonDialog
          hidden={isDeleteDialogHidden}
          onConfirm={onDelete}
          onCancel={toggleDeleteDialog}
          reason={deleteReason?.text}
        />
      )}
    </Card>
  );
});

// eslint-disable-next-line import/no-default-export
export default AppointmentCancellationReasonList;
